// src/components/RandevuListesi.js
//
// Randevuların tam listesi (K7). Takvim yalnızca tarihi olan ve iptal edilmemiş
// randevuları çizer; bu ekran koleksiyonun TAMAMINI okur, böylece
//  - İptal edilen randevular (takvimde gizli) sekmeden erişilebilir olur,
//  - 'start' alanı hiç olmayan v1 kayıtları "Tarihsiz" rozetiyle görünür kalır.
// Koleksiyon küçük olduğu için tek seferde okunur, sıralama/filtre client-side'dır.

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box, Paper, Typography, Tabs, Tab, TextField, InputAdornment, Chip, Stack,
    CircularProgress, Divider, Button, Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EventIcon from '@mui/icons-material/Event';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
    RANDEVU_DURUMLARI,
    normalizeRandevuDurum,
    getRandevuDurumColor,
    getRandevuStart,
    getRandevuEnd
} from './AppointmentConstants';

const formatTarih = (date) => {
    if (!date) return null;
    try { return format(date, 'dd MMM yyyy HH:mm', { locale: tr }); } catch { return null; }
};

const RandevuKarti = ({ randevu }) => {
    const start = getRandevuStart(randevu);
    const end = getRandevuEnd(randevu);
    const plaka = randevu.aracPlaka || 'Plaka Yok';
    const marka = [randevu.aracMarka, randevu.aracModel].filter(Boolean).join(' ');

    return (
        <Paper
            variant="outlined"
            component={RouterLink}
            to={`/randevu/${randevu.id}`}
            sx={{
                p: 1.5, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap',
                textDecoration: 'none', color: 'inherit', '&:hover': { backgroundColor: 'action.hover' }
            }}
        >
            <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                <Typography variant="body2" fontWeight="bold" noWrap>
                    {randevu.customerName || randevu.musteriAdSoyad || 'İsimsiz müşteri'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {plaka}{marka ? ` · ${marka}` : ''}
                </Typography>
            </Box>

            <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                {start ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {formatTarih(start)}{end ? ` → ${formatTarih(end)}` : ''}
                    </Typography>
                ) : (
                    <Chip size="small" color="warning" variant="outlined" label="Tarihsiz kayıt" />
                )}
                {randevu.serviceType && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {randevu.serviceType}
                    </Typography>
                )}
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                {randevu.workOrderId && <Chip size="small" variant="outlined" label="İş emri var" />}
                <Chip size="small" color={getRandevuDurumColor(randevu.durum)} label={normalizeRandevuDurum(randevu.durum)} />
            </Stack>
        </Paper>
    );
};

const RandevuListesi = () => {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('Bekleniyor');
    const [arama, setArama] = useState('');

    useEffect(() => {
        // orderBy KULLANILMIYOR: 'start' alanı olmayan legacy kayıtlar sorgudan düşerdi.
        const unsub = onSnapshot(collection(db, 'appointments'), (snapshot) => {
            setAppointments(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
            setLoading(false);
        }, (error) => {
            console.error('Randevular çekilirken hata:', error);
            setLoading(false);
        });
        return () => unsub();
    }, []);

    const normalized = useMemo(() => (
        appointments
            .map(app => ({ ...app, _durum: normalizeRandevuDurum(app.durum), _start: getRandevuStart(app) }))
            .sort((a, b) => (b._start?.getTime() || 0) - (a._start?.getTime() || 0))
    ), [appointments]);

    const sayilar = useMemo(() => {
        const s = {};
        RANDEVU_DURUMLARI.forEach(d => { s[d] = 0; });
        normalized.forEach(app => { s[app._durum] = (s[app._durum] || 0) + 1; });
        return s;
    }, [normalized]);

    const gorunen = useMemo(() => {
        const q = arama.trim().toLowerCase().replace(/\s/g, '');
        return normalized
            .filter(app => app._durum === tab)
            .filter(app => {
                if (!q) return true;
                const havuz = [
                    app.customerName, app.musteriAdSoyad, app.customerPhone,
                    app.aracPlaka, app.aracMarka, app.aracModel, app.serviceType
                ].filter(Boolean).join(' ').toLowerCase().replace(/\s/g, '');
                return havuz.includes(q);
            });
    }, [normalized, tab, arama]);

    const tarihsizSayisi = normalized.filter(a => !a._start).length;

    if (loading) return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EventIcon color="primary" fontSize="large" />
                    <Box>
                        <Typography variant="h4" component="h1">Randevu Listesi</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Toplam {normalized.length} randevu
                        </Typography>
                    </Box>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Müşteri, plaka veya hizmet ara…"
                        value={arama}
                        onChange={(e) => setArama(e.target.value)}
                        InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
                    />
                    <Button variant="outlined" startIcon={<CalendarMonthIcon />} component={RouterLink} to="/randevular">
                        Takvim
                    </Button>
                </Stack>
            </Box>

            {tarihsizSayisi > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {tarihsizSayisi} randevunun tarih bilgisi eksik. Bu kayıtlar takvimde çizilemez; burada
                    “Tarihsiz kayıt” rozetiyle listelenir.
                </Alert>
            )}

            <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Tabs
                    value={tab}
                    onChange={(e, v) => setTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {RANDEVU_DURUMLARI.map(d => (
                        <Tab key={d} value={d} label={`${d} (${sayilar[d] || 0})`} />
                    ))}
                </Tabs>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={1.5}>
                    {gorunen.length > 0 ? (
                        gorunen.map(app => <RandevuKarti key={app.id} randevu={app} />)
                    ) : (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            Bu sekmede randevu yok.
                        </Typography>
                    )}
                </Stack>
            </Paper>
        </Box>
    );
};

export default RandevuListesi;
